import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { select } from '@panel/commons/core/themev2';
import { sendTrackingInfo } from '@panel/commons';
import { getIsTransitionOrBundleCredit, exportPostingsQualityToExcel } from '@panel/commons/helpers';
import ChevronUp from '@panel/commons/assets/icons/chevronUpOrange.svg';
import ChevronUpCM from '@panel/commons/assets/icons/chevronUpCM.svg';
import ChevronUpVA from '@panel/commons/assets/icons/chevronUpVA.svg';
import ChevronUpUB from '@panel/commons/assets/icons/chevronUpUB.svg';
import ChevronDownOrange from '@panel/commons/assets/icons/chevronDownOrange.svg';
import ChevronDownCM from '@panel/commons/assets/icons/chevronDownCM.svg';
import ChevronDownVA from '@panel/commons/assets/icons/chevronDownVA.svg';
import ChevronDownUB from '@panel/commons/assets/icons/chevronDownUB.svg';
import ChevronDownGray from '@panel/commons/assets/icons/ChevronDown.svg';

import { PostingsSelectors, PostingsOperations } from '../../../../ducks/posting';
import useUrlSearchParams from '../../hooks/useUrlSearchParams';
import { FilterSelectors, FilterOperations } from '../../../../ducks/filter';
import CollapsedWidget from './CollapsedWidget';
import OpenWidget from './OpenWIdget';
import AlertArea from './Alert';
import dashboardDataAdapter from './dashboardDataAdapter';

const siteId = process.env.REACT_APP_BUILD_SITE;

const Dashboard = ({ showPerformanceAlert, postingsError, isIntegrator, isParticular }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLowPerformanceAlert, setShowLowPerformanceAlert] = useState(false);
  const [widgetSelected, setWidgetSelected] = useState('');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { cleanPartialUrlFilters } = useUrlSearchParams();
  const { fetchDashboardInfo, fetchPostingsQualityToExport } = PostingsOperations;
  const {
    dashboardInfo,
    nearEndPostingCount,
    dashboardInfoError,
    postingsQualityToExport,
    postingsQualityToExportCompleted
  } = useSelector(PostingsSelectors.getPostingsState);
  const { appliedFilters } = useSelector(FilterSelectors.getFiltersState);
  const totalReport = dashboardInfo ? dashboardInfo.totalReport : 0;
  const totalDuplicatedPosting = dashboardInfo ? dashboardInfo.totalDuplicatedPosting : 0;
  const totalUnreadLeads = dashboardInfo ? dashboardInfo.totalUnreadLeads : 0;
  const totalPostingQuality = dashboardInfo ? dashboardInfo.totalPostingQuality : 0;
  const leadsCallback = () => {
    window.location.href = `${window.location.origin}/panel/interesados`;
    sendTrackingInfo('Panel', 'Mis avisos', `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Ver Consultas`);
  };
  const duplicatesCallback = () => {
    dispatch(FilterOperations.partialResetFilters('hasDuplicates', { id: 'true', displayName: 'duplicated' }));
    cleanPartialUrlFilters('true', 'hasDuplicates');
    sendTrackingInfo(
      'Panel',
      'Mis avisos',
      `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Mostrar duplicados`
    );
  };
  const qualityCallback = () => {
    dispatch(FilterOperations.partialResetFilters('hasQuality', { id: 'true', displayName: 'quality' }));
    cleanPartialUrlFilters('true', 'hasQuality');
    sendTrackingInfo(
      'Panel',
      'Mis avisos',
      `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Mostrar a completar`
    );
  };
  const reportsCallback = () => {
    dispatch(FilterOperations.partialResetFilters('hasReports', { id: 'true', displayName: 'reports' }));
    cleanPartialUrlFilters('true', 'hasReports');
    sendTrackingInfo('Panel', 'Mis avisos', `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Mostrar reportes`);
  };
  const reportsTotalCallback = () => {
    dispatch(FilterOperations.partialResetFilters('hasReportsTotal', { id: 'true', displayName: 'reportsTotal' }));
    cleanPartialUrlFilters('true', 'hasReportsTotal');
    sendTrackingInfo(
      'Panel',
      'Mis avisos',
      `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Mostrar avisos reportados totales`
    );
  };
  const nearEndCallback = () => {
    dispatch(FilterOperations.partialResetFilters('status', { id: 'NEAR_END', displayName: 'NEAR_END' }));
    cleanPartialUrlFilters('NEAR_END', 'status');
    sendTrackingInfo(
      'Panel',
      'Mis avisos',
      `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Mostrar por vencer`
    );
  };
  const lowPerformanceAlertCallback = () => {
    Cookies.set('ignorarAlertDashboard', true, { expires: 1 });
    setShowLowPerformanceAlert(false);
    sendTrackingInfo(
      'Panel',
      'Mis avisos',
      `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Ignorar desempeño`
    );
  };
  const downloadPostingsQualityCallback = () => {
    dispatch(fetchPostingsQualityToExport());
  };

  const applySelectedWidget = filters => {
    if (filters.hasQuality.displayName) return setWidgetSelected(filters.hasQuality.displayName);
    if (filters.hasDuplicates.displayName) return setWidgetSelected(filters.hasDuplicates.displayName);
    if (filters.hasReports.displayName) return setWidgetSelected(filters.hasReports.displayName);
    return setWidgetSelected('');
  };

  useEffect(() => {
    applySelectedWidget(appliedFilters);
  }, [appliedFilters.hasQuality, appliedFilters.hasDuplicates, appliedFilters.hasReports, appliedFilters]);

  const dashboardData = dashboardDataAdapter(
    totalUnreadLeads,
    leadsCallback,
    totalPostingQuality,
    qualityCallback,
    totalDuplicatedPosting,
    duplicatesCallback,
    totalReport,
    reportsCallback,
    reportsTotalCallback,
    downloadPostingsQualityCallback
  );

  // VALIDACIÓN PARA CMBR
  const callDashboard = siteId !== 'CMBR';

  const alertDashboardDisabled = Cookies.get('ignorarAlertDashboard');

  useEffect(() => {
    if (callDashboard) {
      dispatch(fetchDashboardInfo());
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [dispatch, fetchDashboardInfo]);

  useEffect(() => {
    if (showPerformanceAlert && !nearEndPostingCount && !alertDashboardDisabled) {
      setShowLowPerformanceAlert(true);
    }
  }, [nearEndPostingCount, showPerformanceAlert, alertDashboardDisabled]);

  useEffect(() => {
    if (isIntegrator) {
      setIsOpen(true);
    }
  }, [isIntegrator]);

  useEffect(() => {
    sendTrackingInfo('Panel', 'Mis avisos', `Dashboard-${isOpen ? 'Contraer' : 'Deplegar'}`);
  }, [isOpen]);

  useEffect(() => {
    if (postingsQualityToExportCompleted && postingsQualityToExport.postings.length > 0) {
      const adaptedExportData =
        postingsQualityToExport &&
        postingsQualityToExport.postings.map(postingData => {
          const isTransitionOrBundleCredit =
            postingData.publicationPlanId && getIsTransitionOrBundleCredit(postingData.publicationPlanId);
          const dataToPostingsExport = {
            [t('panel.quality.generalData.IdAviso')]: postingData.postingId,
            [t('panel.quality.generalData.codigo')]: postingData.code,
            [t('panel.quality.generalData.tipoPropiedad')]: postingData.realEstateTypeName,
            [t('panel.quality.generalData.tituloAviso')]: postingData.title,
            [t('panel.quality.generalData.precio')]: postingData.opTypeAndPrices,
            [t('panel.quality.generalData.fechaInicio')]: postingData.beginDate,
            [t('panel.quality.generalData.plan')]: isTransitionOrBundleCredit
              ? postingData.publicationPlanName
              : t(`panel.avisos.planName.${postingData.publicationPlan}`),
            [t('panel.quality.generalData.updateLink')]: `${window.location.origin}${postingData.urlEditPosting}`,
            [t('panel.quality.generalData.porcentajePanoramix')]: postingData.percentageCorrectness
          };
          for (let i = 1; i <= postingsQualityToExport.maxTips; i += 1) {
            if (i <= postingsQualityToExport.maxTips) {
              dataToPostingsExport[`${t('panel.quality.generalData.consejoPanoramix', { value: i })}`] = postingData
                .qualityTips[`${i} Advice`]
                ? postingData.qualityTips[`${i} Advice`]
                : '';
            }
          }
          return dataToPostingsExport;
        });

      const newDate = `${new Date().getFullYear()}${new Date().getMonth() + 1}${new Date().getDate()}`;
      const filName = `${t('panel.quality.generalData.DocumentName')}-${newDate}`;
      exportPostingsQualityToExcel(adaptedExportData, filName);
    }

    if (postingsQualityToExportCompleted) {
      sendTrackingInfo(
        'Panel',
        'Mis avisos',
        `Dashboard-Accionables-${isOpen ? 'Abierto' : 'Cerrado'}-Descargar Avisos`
      );
      Cookies.set('exportPostingsQualityToExcel', true, { expires: 1 });
    }
  }, [postingsQualityToExportCompleted]);

  return (
    <>
      <Container hasData={dashboardInfo && !dashboardInfoError}>
        <ContentArea>
          <TopArea isOpen={isOpen} data-tour-panel='dashboard'>
            <Title isOpen={isOpen} data-panel='panel-dashboard-title'>
              {t('panel.dashboard.title')}
            </Title>
            <CollapsedWidgetArea isOpen={isOpen}>
              {dashboardData.map(widget => (
                <CollapsedWidget key={widget.name} widget={widget} isSelected={widget.name === widgetSelected} />
              ))}
            </CollapsedWidgetArea>
            <Chevron isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} data-panel='panel-dashboard-chevron' />
          </TopArea>
          <BottomArea isOpen={isOpen}>
            {dashboardData.map((widget, i) => (
              <ContainerOpenWidget key={widget.name}>
                <OpenWidget widget={widget} isSelected={widget.name === widgetSelected} isParticular={isParticular} />
                {i < 3 && <WidgetSeparator />}
              </ContainerOpenWidget>
            ))}
          </BottomArea>
        </ContentArea>
        {(nearEndPostingCount > 0 || showLowPerformanceAlert) && (
          <AlertArea
            ctaCallback={nearEndPostingCount ? nearEndCallback : lowPerformanceAlertCallback}
            nearEndPostingCount={nearEndPostingCount}
          />
        )}
      </Container>
      {!postingsError && <Separator />}
    </>
  );
};

const Container = styled.div`
  width: 100%;
  display: ${({ hasData }) => (hasData ? 'flex' : 'none')};
  flex-direction: column;
  border-radius: 12px;
  background-color: ${select('lightGray')};
`;
const ContentArea = styled.div`
  width: 100%;
  min-height: 58px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  padding: 11px 0;
`;

const Separator = styled.div`
  width: 100%;
  border-bottom: 1px solid ${select('lightGray')};
  margin: 17px 0;
`;

const TopArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ isOpen }) => isOpen && '9px'};
  padding-left: 29px;
  padding-right: 25px;
`;

const Title = styled.div`
  font-family: Hind;
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  color: ${({ isOpen }) => !isOpen && select('darkGray')};
  margin-right: 66px;
  margin-top: ${({ isOpen }) => (!isOpen ? '0' : '6px')};
`;

const getChevronUpColor = () => {
  if (siteId === 'CMBR') return ChevronUpCM;
  if (siteId === 'UBPE') return ChevronUpUB;
  if (siteId === 'VAMX') return ChevronUpVA;
  return ChevronUp;
};

const getChevronDownColor = () => {
  if (siteId === 'CMBR') return ChevronDownCM;
  if (siteId === 'UBPE') return ChevronDownUB;
  if (siteId === 'VAMX') return ChevronDownVA;
  return ChevronDownOrange;
};

const Chevron = styled.button`
  width: 23px;
  height: 23px;
  background-image: ${({ isOpen }) => `url(${isOpen ? getChevronUpColor() : ChevronDownGray})`};
  :hover {
    background-image: ${({ isOpen }) => `url(${isOpen ? getChevronUpColor() : getChevronDownColor()})`};
  }
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  cursor: pointer;
  position: relative;
`;

const CollapsedWidgetArea = styled.div`
  height: 100%;
  flex: 1;
  display: ${({ isOpen }) => (isOpen ? 'none' : 'flex')};
  align-items: center;
`;

const BottomArea = styled.div`
  width: 100%;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
`;

const ContainerOpenWidget = styled.div`
  display: flex;
  align-items: center;
`;

const WidgetSeparator = styled.div`
  height: 110px;
  border-right: 1px solid ${select('middleGray')};
  display: block;
  margin-left: 38px;
  @media (max-width: 1350px) {
    margin-left: 15px;
  }
  @media (max-width: 1260px) {
    margin-left: 20px;
  }
  @media (max-width: 1080px) {
    margin-left: 10px;
  }
`;

export default Dashboard;
